// components/Sidebar.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  Divider,
  ListItemButton,
  Box,
  Typography,
  useMediaQuery,
  AppBar,
  Toolbar,
  IconButton,
  Menu as MuiMenu,
  MenuItem,
  Tooltip,
  CircularProgress,
  Stack,
} from "@mui/material";

import {
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  AccountCircle as AccountCircleIcon,
  ListAlt,
} from "@mui/icons-material";

import { Settings as SettingsIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import useLogout from "../../../hooks/authentication_hooks/useLogout";
import kloudLogo from "../../../assets/kloud-web-logo-blue.svg";

const DRAWER_WIDTH = 280;
const ICON_PX = 25;
const LABEL_PX = 13;
const ITEM_PY = 0.8;
const ITEM_PX = 3;

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { handleLogout } = useLogout();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [signingOut, setSigningOut] = useState(false);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);

  const items = useMemo(
    () => [
      {
        label: "Dashboard",
        Icon: DashboardIcon,
        key: "dashboard",
        path: "/dashboard",
      },
      { label: "Requests", Icon: ListAlt, key: "requests", path: "/requests" },
    ],
    [],
  );

  const profileMenuOpen = Boolean(profileAnchorEl);
  const openProfileMenu = (e) => setProfileAnchorEl(e.currentTarget);
  const closeProfileMenu = () => setProfileAnchorEl(null);

  useEffect(() => {
    const all = [...items, { key: "profile", path: "/profile" }];
    const selected = all.find((item) => item.path === location.pathname);
    setSelectedItem(selected ? selected.key : null);
  }, [location, items]);

  // cross-tab logout sync
  useEffect(() => {
    const logout = async () => {
      if (typeof auth.removeUser === "function") await auth.removeUser();
      if (typeof auth.clearStaleState === "function")
        await auth.clearStaleState();
    };

    let bc;
    if ("BroadcastChannel" in window) {
      bc = new BroadcastChannel("auth");
      bc.onmessage = (e) => e.data === "logout" && logout();
    }

    const onStorage = (e) => {
      const loggingOut = sessionStorage.getItem("logoutInProgress") === "1";
      if (e.key === "authState" && e.newValue === "signOut") {
        if (!loggingOut) logout();
      }
    };

    window.addEventListener("storage", onStorage);
    return () => {
      bc?.close?.();
      window.removeEventListener("storage", onStorage);
    };
  }, [auth]);

  const handleItemClick = (key, path) => {
    setSelectedItem(key);
    if (isMobile) setMobileOpen(false);
    navigate(path);
  };

  const signOut = async () => {
    try {
      setSigningOut(true);

      localStorage.setItem("authState", "signOut");
      if ("BroadcastChannel" in window) {
        const bc = new BroadcastChannel("auth");
        bc.postMessage("logout");
        bc.close();
      }

      await handleLogout();
      setTimeout(() => setSigningOut(false), 800);
    } catch (err) {
      console.error("Logout failed:", err);
      setSigningOut(false);
    }
  };

  const goProfile = () => {
    closeProfileMenu();
    handleItemClick("profile", "/profile");
  };

  /* ---------------- Drawer List Content ---------------- */
  const drawerContent = (
    <Box
      sx={{
        width: DRAWER_WIDTH,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        backgroundColor: "white",
        color: "black",
      }}
    >
      {/* top half */}
      <Box>
        <Toolbar />

        <Divider sx={{ mx: 0, borderColor: "rgba(0,0,0,0.08)" }} />

        <Box sx={{ height: 16 }} />

        <List dense disablePadding sx={{ px: 2 }}>
          {items.map((item) => {
            const selected = selectedItem === item.key;
            return (
              <ListItemButton
                key={item.key}
                onClick={() => handleItemClick(item.key, item.path)}
                selected={selected}
                sx={{
                  py: ITEM_PY,
                  px: ITEM_PX,
                  mx: 2,
                  borderRadius: 1,
                  borderLeft: selected
                    ? "3px solid #00A3E0"
                    : "3px solid transparent",
                  backgroundColor: selected
                    ? "rgba(0,163,224,0.08)"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: selected
                      ? "rgba(0,163,224,0.12)"
                      : "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 28,
                    color: selected ? "#00A3E0" : "#64748B",
                  }}
                >
                  <item.Icon sx={{ fontSize: ICON_PX }} />
                </ListItemIcon>

                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    sx: {
                      fontSize: LABEL_PX,
                      fontWeight: selected ? 600 : 500,
                      color: selected ? "#1E293B" : "#64748B",
                    },
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      {/* bottom section */}
      <Box sx={{ px: 2, pb: 3, pt: 2 }}>
        {/* Privacy + Contact links */}
        <Stack
          direction="row"
          justifyContent="center"
          spacing={2.5}
          sx={{ mb: 1.5 }}
        >
          <Typography
            component="a"
            href="/privacy-policy"
            sx={{
              fontSize: 11,
              color: "#94A3B8",
              fontWeight: 500,
              textDecoration: "none",
              cursor: "pointer",
              transition: "color 0.15s",
              "&:hover": { color: "#00A3E0" },
            }}
          >
            Privacy Policy
          </Typography>
          <Typography
            component="a"
            href="/contact"
            sx={{
              fontSize: 11,
              color: "#94A3B8",
              fontWeight: 500,
              textDecoration: "none",
              cursor: "pointer",
              transition: "color 0.15s",
              "&:hover": { color: "#00A3E0" },
            }}
          >
            Contact
          </Typography>
        </Stack>

        <Divider sx={{ borderColor: "rgba(0,0,0,0.08)", mb: 1.5 }} />

        <Typography
          sx={{
            fontSize: 10,
            textAlign: "center",
            color: "#94A3B8",
            fontWeight: 400,
          }}
        >
          © 2025 Kloud Scan
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Header */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={(t) => ({
          zIndex: t.zIndex.drawer + 1,
          backgroundColor: "white",
          color: "black",
          width: "100%",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
        })}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          {isMobile && (
            <IconButton
              edge="start"
              sx={{ color: "black", mr: 1 }}
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box
            onClick={() => navigate("/dashboard")}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              position: { xs: "absolute", md: "relative" },
              left: { xs: "50%", md: "auto" },
              transform: { xs: "translateX(-50%)", md: "none" },
            }}
          >
            <Box
              component="img"
              src={kloudLogo}
              alt="Kloud Scan"
              sx={{
                height: { xs: 50, sm: 50, md: 50 },
                width: "auto",
              }}
            />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {signingOut ? (
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CircularProgress size={20} sx={{ color: "#93c5fd" }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "black" }}
              >
                Signing out…
              </Typography>
            </Stack>
          ) : (
            <>
              <Tooltip title="Account">
                <IconButton
                  onClick={openProfileMenu}
                  size="large"
                  sx={{ color: "black" }}
                >
                  <AccountCircleIcon />
                </IconButton>
              </Tooltip>

              <MuiMenu
                anchorEl={profileAnchorEl}
                open={profileMenuOpen}
                onClose={closeProfileMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: {
                    minWidth: 220,
                    borderRadius: 2,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    py: 1,
                  },
                }}
              >
                {/* User Info Header */}
                <Box
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderBottom: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      fontSize: 12,
                    }}
                  >
                    Account
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.primary",
                      mt: 0.25,
                      fontSize: 13,
                      wordBreak: "break-all",
                    }}
                  >
                    {auth.user?.profile?.email || "email@example.com"}
                  </Typography>
                </Box>

                {/* Menu Items */}
                <MenuItem
                  onClick={goProfile}
                  sx={{
                    py: 1.5,
                    px: 3,
                    fontSize: 14,
                    fontWeight: 500,
                    borderRadius: 1,
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                    color: "text.primary",
                  }}
                >
                  <SettingsIcon
                    sx={{ mr: 1.5, fontSize: 18, color: "text.secondary" }}
                  />
                  Profile
                </MenuItem>

                <MenuItem
                  onClick={signOut}
                  sx={{
                    py: 1.5,
                    px: 3,
                    fontSize: 14,
                    fontWeight: 500,
                    borderRadius: 1,
                    color: "text.primary",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.04)",
                      color: "#b91c1c",
                      "& .MuiSvgIcon-root": {
                        color: "#b91c1c",
                      },
                    },
                  }}
                >
                  <LogoutIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  Sign Out
                </MenuItem>
              </MuiMenu>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            backgroundColor: "white",
            borderRight: "1px solid rgba(0,0,0,0.12)",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
